import { useApolloClient } from "@apollo/client";
import { HasInternalRole, useAccount } from "@qw/auth";
import { File_Audience, File_Source } from "@qw/graphql";
import { isInternal } from "@qw/qw-common";
import { Dialog, uploadFiles, useModal, useToast } from "@qw/ui";
import { createContext, type FC, type PropsWithChildren, useContext, useState } from "react";
import Dropzone, { type DropzoneState, type FileWithPath } from "react-dropzone";
import { useInsuredFiles } from "../../hooks";
import { AudienceField, audienceSchema } from "./audience-field";
import { EditFileDialog } from "./edit-file-dialog";

const OpenFilePickerContext = createContext<(() => void) | null>(null);

export const useOpenFilePicker = () => {
  const openFilePicker = useContext(OpenFilePickerContext);

  if (!openFilePicker) {
    throw new Error("useOpenFilePicker must be used within an ExternalDropzone");
  }

  return openFilePicker;
};

export type ExternalDropzoneProps = PropsWithChildren & {
  className?: string;
};

export const ExternalDropzone: FC<ExternalDropzoneProps> = ({ children, className }) => {
  const { insuredId } = useInsuredFiles();
  const { toast } = useToast();
  const { openForm } = useModal();
  const [fileId, setFileId] = useState<string | undefined>(undefined);
  const { user } = useAccount();
  const client = useApolloClient();
  const refetch = () => client.refetchQueries({ include: ["FilesByInsured", "FilesByLabel", "FileTags"] });
  const internal = isInternal(user);

  const upload = async (files: FileWithPath[], audience: File_Audience) => {
    const single = files.length === 1;
    const errorTitle = single ? "Error uploading file" : `Error uploading ${files.length} files`;

    toast({ title: "Loading...." });
    try {
      const { fileId: fileIds } = await uploadFiles([...files], {
        insuredId,
        audience,
        source: File_Source.ManualUpload,
      });

      if (fileIds) {
        toast({ title: single ? "File uploaded successfully" : `${files.length} files uploaded` });
        if (single) {
          setFileId(fileIds[0]);
        }
      } else {
        toast({ title: errorTitle });
      }
    } catch (error) {
      toast({
        title: errorTitle,
        description: error instanceof Error ? error.message : "Please try again",
        variant: "destructive",
      });
    } finally {
      void refetch();
    }
  };

  // Agents never choose an audience, and a single file follows up with EditFileDialog,
  // so only an internal user's batch asks for it up front.
  const chooseAudience = async (files: FileWithPath[]) => {
    if (!internal || files.length === 1) {
      return File_Audience.External;
    }

    const values = await openForm(<AudienceField />, {
      title: `Upload ${files.length} files`,
      description: "Select who should have access to these files",
      validationSchema: audienceSchema,
      defaultValues: { audience: File_Audience.External },
    });

    return values?.audience;
  };

  return (
    <Dropzone
      // Keep `noClick`: the dropzone wraps the whole file list, so clicking any
      // row would open a picker. Reach it through `useOpenFilePicker` instead.
      noClick
      onDrop={async (files: FileWithPath[]) => {
        if (files.length === 0) {
          return;
        }

        const audience = await chooseAudience(files);

        if (audience) {
          await upload(files, audience);
        }
      }}
    >
      {({ getRootProps, getInputProps, open }: DropzoneState) => (
        <div {...getRootProps()} className={className}>
          <input {...getInputProps()} />
          <OpenFilePickerContext.Provider value={open}>{children}</OpenFilePickerContext.Provider>
          <HasInternalRole>
            <Dialog onOpenChange={() => setFileId(undefined)} open={fileId ? true : false}>
              {fileId && <EditFileDialog fileId={fileId} onClose={() => setFileId(undefined)} />}
            </Dialog>
          </HasInternalRole>
        </div>
      )}
    </Dropzone>
  );
};
